// The one place the brand lives: name, domain, colors, fonts, logo and the few lines we say
// everywhere. The name is TabYeah. The live site is the domain below (www redirects to the apex).
//
// The app, the server's pages, the icon, the favicon, the wordmarks, the manifest, the share texts
// and the payment notes all read from here. Anything outside the app (video renders, recaps, design
// files) can fetch /brand.json or /brand.css from the live site, or run `npm run brand:export`.
// test/brand.test.ts fails if the name, accent color or fonts show up hard-coded anywhere else.
// The logo paths live in brand-art.ts (no name, no color). This file paints them.

import {
  FAVICON_DOTS, FAVICON_Y, ICON_DOTS, ICON_SLIP, ICON_Y,
  WORDMARK_BLOCK, WORDMARK_DOTS, WORDMARK_TAB, WORDMARK_VIEWBOX, WORDMARK_YEAH,
} from './brand-art.ts';

export type Palette = {
  /** Page ground. */ bg: string;
  /** Cards, inputs. */ card: string;
  /** Main text. */ ink: string;
  /** Secondary text. */ ink2: string;
  /** Hints and labels. */ ink3: string;
  line: string;
  line2: string;
  chip: string;
  /** Buttons and links. Espresso in light mode, paper in dark. Not yellow. */ accent: string;
  accentHover: string;
  /** Text on an accent wash. */ accentDeep: string;
  accentWash: string;
  /** Text on an accent button. */ onAccent: string;
  /** Money still owed. Gray plus the word, never its own hue. */ owed: string;
  owedWash: string;
  /** Dark hero blocks. */ hero: string;
  onHero: string;
  onHero2: string;
  scrim: string;
  /** Highlighter behind totals, and the YEAH block. Never used as text. */ highlight: string;
  /** Espresso on the highlighter. Never white. */ onHighlight: string;
  /** Red pen for the word "Settled" and its tick. */ settled: string;
};

export type Brand = {
  /** The name as people say and type it. */
  name: string;
  /** How a voice-over says the name, so text-to-speech reads it right. */
  spoken: string;
  /**
   * Unused by the drawn logo. Kept so a typed fallback still type-checks. The logo is outlined art
   * from wordmarkSvg / wordmarkHtml, not letters set in a font.
   */
  wordmark: { main: string; accent: string };
  /** The public domain, for print and copy. Live links use the address the page was opened on. */
  domain: string;
  /** One line under the name, for the home screen and link previews. */
  description: string;
  /** The positioning line from docs/brand.md. */
  oneLiner: string;
  /** The rallying cry, for posts and videos. Not shown in the app yet. */
  cheer: string;
  /** Where people reach us about privacy. */
  privacyEmail: string;
  colors: { light: Palette; dark: Palette };
  fonts: { display: string; body: string; mono: string; /** One Google Fonts stylesheet that loads all three. */ href: string };
};

export const brand: Brand = {
  name: 'TabYeah',
  spoken: 'tab yeah',
  wordmark: { main: 'TabYeah', accent: '' },
  domain: 'tabyeah.com',
  description: 'You got the bill. Friends tap what they had and pay you back.',
  oneLiner: 'You got the bill. Your friends scan, tap what they had, and pay you back, tax and tip included. No app for them.',
  cheer: 'Tab? Yeah.',
  privacyEmail: 'privacy@carteblanche.com',
  colors: {
    light: {
      bg: '#FAF8F4', card: '#FFFFFF', ink: '#1F1512', ink2: '#6B5A52', ink3: '#6B5A52',
      line: '#E6E1D8', line2: '#EFEBE4', chip: '#F3F0EA',
      accent: '#1F1512', accentHover: '#3A2C27', accentDeep: '#1F1512', accentWash: '#EDE6DE', onAccent: '#FFFFFF',
      owed: '#6B5A52', owedWash: '#F3EBE4',
      hero: '#1F1512', onHero: '#FAF8F4', onHero2: '#CDBFB6',
      scrim: 'rgba(31,21,18,.55)',
      highlight: '#FFE14D', onHighlight: '#1F1512', settled: '#C7321A',
    },
    dark: {
      bg: '#1F1512', card: '#2B201C', ink: '#FAF8F4', ink2: '#CDBFB6', ink3: '#CDBFB6',
      line: '#3D302A', line2: '#342822', chip: '#342822',
      accent: '#FAF8F4', accentHover: '#FFFFFF', accentDeep: '#FAF8F4', accentWash: '#3A2E28', onAccent: '#1F1512',
      owed: '#CDBFB6', owedWash: '#3A2E28',
      hero: '#FAF8F4', onHero: '#1F1512', onHero2: '#6B5A52',
      scrim: 'rgba(0,0,0,.6)',
      highlight: '#FFE14D', onHighlight: '#1F1512', settled: '#FF7A5C',
    },
  },
  fonts: {
    display: '"Bricolage Grotesque"',
    body: 'Figtree',
    mono: '"JetBrains Mono"',
    href: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=Figtree:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&display=swap',
  },
};

/** "Lupa · TabYeah": the page title for a screen. */
export const titled = (page: string): string => (page ? `${page} · ${brand.name}` : brand.name);

const CSS_VARS: [keyof Palette, string][] = [
  ['bg', '--bg'], ['card', '--card'], ['ink', '--ink'], ['ink2', '--ink-2'], ['ink3', '--ink-3'],
  ['line', '--line'], ['line2', '--line-2'], ['chip', '--chip'],
  ['accent', '--accent'], ['accentHover', '--accent-hover'], ['accentDeep', '--accent-deep'], ['accentWash', '--accent-wash'], ['onAccent', '--on-accent'],
  ['owed', '--owed'], ['owedWash', '--owed-wash'],
  ['hero', '--hero'], ['onHero', '--on-hero'], ['onHero2', '--on-hero-2'], ['scrim', '--scrim'],
  ['highlight', '--highlight'], ['onHighlight', '--on-highlight'], ['settled', '--settled'],
];
const vars = (p: Palette): string => CSS_VARS.map(([k, v]) => `${v}:${p[k]};`).join(' ');

/** The design tokens as CSS variables, light and dark. app.css uses only these. */
export function brandCss(b: Brand = brand): string {
  return `/* Made from src/brand.ts. Change the brand there, not here. */
:root{ ${vars(b.colors.light)}
  --f-display:${b.fonts.display},system-ui,sans-serif;
  --f-body:${b.fonts.body},system-ui,-apple-system,sans-serif;
  --f-mono:${b.fonts.mono},ui-monospace,Menlo,monospace;
  color-scheme:light; }
@media (prefers-color-scheme: dark){ :root{ ${vars(b.colors.dark)} color-scheme:dark; } }
`;
}

export const escHtml = (s: string): string => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const circles = (dots: ReadonlyArray<readonly [number, number, number]>, fill: string): string =>
  dots.map(([cx, cy, r]) => `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}"/>`).join('');

export type WordmarkMode = 'light' | 'dark' | 'black';

/**
 * Receipt Line wordmark. Light and dark paint from the palette. Black is one ink for stamps.
 * YEAH letters stay onHighlight (espresso) on the highlighter. Never white on yellow.
 * `inline` is for HTML: fills are CSS variables so dark mode follows the page.
 */
export function wordmarkSvg(mode: WordmarkMode | 'inline' = 'light', b: Brand = brand): string {
  const light = b.colors.light;
  const dark = b.colors.dark;
  const paint = mode === 'inline'
    ? { block: 'var(--highlight)', tab: 'var(--ink)', dots: 'var(--ink-2)', yeah: 'var(--on-highlight)' }
    : mode === 'black'
      ? { block: '#000000', tab: '#000000', dots: '#000000', yeah: '#FFFFFF' }
      : mode === 'dark'
        ? { block: light.highlight, tab: dark.ink, dots: dark.ink2, yeah: light.onHighlight }
        : { block: light.highlight, tab: light.ink, dots: light.ink2, yeah: light.onHighlight };
  const box = WORDMARK_BLOCK;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${WORDMARK_VIEWBOX}" width="495" height="94" role="img" aria-label="${escHtml(b.name)}"><rect x="${box.x}" y="${box.y}" width="${box.width}" height="${box.height}" fill="${paint.block}"/><path d="${WORDMARK_TAB}" fill="${paint.tab}"/>${circles(WORDMARK_DOTS, paint.dots)}<path d="${WORDMARK_YEAH}" fill="${paint.yeah}"/></svg>`;
}

/** The logo as HTML. Outlined art, sized by .brand in app.css. Reads as the brand name. */
export const wordmarkHtml = (b: Brand = brand): string => wordmarkSvg('inline', b);

/** Big Y Slip: the home-screen icon. Square, not pre-rounded. Background is the accent (espresso). */
export function iconSvg(b: Brand = brand): string {
  const p = b.colors.light;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128" width="1024" height="1024" role="img" aria-label="${escHtml(b.name)}"><rect width="128" height="128" fill="${p.accent}"/><path d="${ICON_SLIP}" fill="${p.bg}"/><rect x="36" y="20" width="56" height="62" fill="${p.highlight}"/><path d="${ICON_Y}" fill="${p.onHighlight}"/>${circles(ICON_DOTS, p.ink2)}</svg>`;
}

/** Receipt Line favicon: highlighted Y and four dots. */
export function faviconSvg(b: Brand = brand): string {
  const p = b.colors.light;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" role="img" aria-label="${escHtml(b.name)}"><rect width="32" height="32" rx="6" fill="${p.bg}"/><rect x="5" y="3" width="22" height="24" fill="${p.highlight}"/><path d="${FAVICON_Y}" fill="${p.accent}"/>${circles(FAVICON_DOTS, p.accent)}</svg>`;
}

/** What a phone uses when the app is added to the home screen. */
export function manifest(b: Brand = brand) {
  return {
    name: b.name, short_name: b.name,
    // Relative, so a phone opens the host it was installed from. On the live site that host is the domain above.
    id: `https://${b.domain}/`, start_url: '/', scope: '/', display: 'standalone',
    background_color: b.colors.light.bg, theme_color: b.colors.light.accent,
    icons: [
      { src: '/icon-192.png', sizes: '192x192', type: 'image/png', purpose: 'any' },
      { src: '/icon-512.png', sizes: '512x512', type: 'image/png', purpose: 'any' },
      { src: '/icon-maskable-192.png', sizes: '192x192', type: 'image/png', purpose: 'maskable' },
      { src: '/icon-maskable-512.png', sizes: '512x512', type: 'image/png', purpose: 'maskable' },
      { src: '/icon.svg', sizes: 'any', type: 'image/svg+xml', purpose: 'any' },
    ],
  };
}

/** Fills {{brand.name}}-style blanks in the server's HTML pages. Unknown blanks are left as is. */
export function fillBrand(html: string, b: Brand = brand): string {
  const values: Record<string, string> = {
    name: b.name, domain: b.domain, description: b.description, privacyEmail: b.privacyEmail,
    fontsHref: b.fonts.href, bg: b.colors.light.bg, bgDark: b.colors.dark.bg,
  };
  const filled = html.replace(/\{\{brand\.wordmark\}\}/g, wordmarkHtml(b));
  return filled.replace(/\{\{brand\.(\w+)\}\}/g, (m, k: string) => (k in values ? escHtml(values[k]) : m));
}
